import { useTranslation } from 'react-i18next'
import useDialog from '../hooks/useDialog'
import {
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  IconButtonProps,
  Stack,
  TextField,
  Tooltip
} from '@mui/material'
import { CatalogProduct, CatalogProductInput } from '../types'
import { Edit } from '@mui/icons-material'
import { LoadingButton } from '@mui/lab'
import { useEffect } from 'react'
import { updateCatalogProduct } from '../services/catalogProductService'
import { useDispatch } from 'react-redux'
import { createErrorNotification, createSuccessNotification } from '../redux/slices/notifications'
import * as Yup from 'yup'
import { isRequired, mustBeLessThanCharacters, mustBeString } from '../utils/validation'
import { FormikHelpers, getIn, useFormik } from 'formik'

export interface CatalogProductFieldEditButtonProps extends Omit<
  IconButtonProps,
  'children' | 'onClick' | 'onChange'
> {
  catalogProduct: CatalogProduct
  field: string
  title?: string
  onUpdate?: (catalogProduct: CatalogProduct) => void
}

export default function CatalogProductFieldEditButton({
  catalogProduct,
  field,
  title,
  onUpdate,
  ...props
}: CatalogProductFieldEditButtonProps) {
  const { t } = useTranslation('catalogProduct')

  const dispatch = useDispatch()

  const { open, openDialog, closeDialog } = useDialog()

  const initialValues: CatalogProductInput = {
    type: catalogProduct.type,
    name: catalogProduct.name,
    manufacturer: catalogProduct.manufacturer,
    manufacturerProductCode: catalogProduct.manufacturerProductCode,
    status: catalogProduct.status,
    customData: catalogProduct.customData
  }

  const validationSchema = Yup.object().shape({
    name: Yup.string()
      .typeError(mustBeString(t('catalogProduct:name')))
      .required(isRequired(t('catalogProduct:name'))),

    manufacturer: Yup.string()
      .typeError(mustBeString(t('catalogProduct:manufacturer')))
      .max(255, mustBeLessThanCharacters(t('catalogProduct:manufacturer'), 255))
      .optional()
      .nullable(),

    manufacturerProductCode: Yup.string()
      .typeError(mustBeString(t('catalogProduct:manufacturerProductCode')))
      .max(255, mustBeLessThanCharacters(t('catalogProduct:manufacturerProductCode'), 255))
      .optional()
      .nullable()
  })

  const {
    values,
    touched,
    errors,
    isSubmitting,
    resetForm,
    handleBlur,
    handleChange,
    handleSubmit
  } = useFormik({
    initialValues,
    validationSchema,
    onSubmit
  })

  useEffect(() => {
    if (!open) {
      const timeout = setTimeout(() => resetForm(), 300)
      return () => clearTimeout(timeout)
    }
  }, [open])

  const isError = (field: string) => {
    const fieldTouched = getIn(touched, field)
    const fieldError = getIn(errors, field)

    return Boolean(fieldTouched && fieldError)
  }

  const getHelperText = (field: string) => {
    const fieldTouched = getIn(touched, field)
    const fieldError = getIn(errors, field)

    return fieldTouched && fieldError ? fieldError : undefined
  }

  async function onSubmit(
    values: CatalogProductInput,
    { setSubmitting }: FormikHelpers<CatalogProductInput>
  ) {
    try {
      setSubmitting(true)

      const updatedCatalogProduct = await updateCatalogProduct(catalogProduct.id, values)

      dispatch(createSuccessNotification(t('catalogProductUpdateSuccess')))
      onUpdate?.(updatedCatalogProduct)
    } catch (_) {
      // TODO: Handle server-side validation errors.
      dispatch(createErrorNotification(t('catalogProductUpdateError')))
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <>
      <Tooltip title={t('actions:edit')}>
        <IconButton {...props} onClick={openDialog}>
          <Edit color="action" />
        </IconButton>
      </Tooltip>

      <Dialog open={open} maxWidth="sm" fullWidth onClose={closeDialog}>
        <DialogTitle>{title ?? t('editCatalogProduct')}</DialogTitle>
        <DialogContent>
          <Box mt={2}>
            <form onSubmit={handleSubmit}>
              <Grid container spacing={6}>
                <Grid item xs={12}>
                  {field === 'name' && (
                    <TextField
                      name="name"
                      value={values.name}
                      label={t('name')}
                      error={isError('name')}
                      helperText={getHelperText('name')}
                      disabled={isSubmitting}
                      autoFocus
                      fullWidth
                      onBlur={handleBlur}
                      onChange={handleChange}
                    />
                  )}

                  {field === 'manufacturer' && (
                    <TextField
                      name="manufacturer"
                      value={values.manufacturer}
                      label={t('manufacturer')}
                      error={isError('manufacturer')}
                      helperText={getHelperText('manufacturer')}
                      disabled={isSubmitting}
                      autoFocus
                      fullWidth
                      onBlur={handleBlur}
                      onChange={handleChange}
                    />
                  )}

                  {field === 'manufacturerProductCode' && (
                    <TextField
                      name="manufacturerProductCode"
                      value={values.manufacturerProductCode}
                      label={t('manufacturerProductCode')}
                      error={isError('manufacturerProductCode')}
                      helperText={getHelperText('manufacturerProductCode')}
                      disabled={isSubmitting}
                      autoFocus
                      fullWidth
                      onBlur={handleBlur}
                      onChange={handleChange}
                    />
                  )}
                </Grid>

                <Grid item xs={12}>
                  <Stack direction="row" justifyContent="flex-end" alignItems="center" spacing={2}>
                    <Button
                      variant="text"
                      color="inherit"
                      disabled={isSubmitting}
                      onClick={closeDialog}
                    >
                      {t('actions:cancel')}
                    </Button>

                    <LoadingButton
                      type="submit"
                      variant="contained"
                      color="primary"
                      loading={isSubmitting}
                    >
                      {t('actions:save')}
                    </LoadingButton>
                  </Stack>
                </Grid>
              </Grid>
            </form>
          </Box>
        </DialogContent>
      </Dialog>
    </>
  )
}
